import {
  Box,
  HStack,
  IconButton,
  Image,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  useDisclosure,
} from '@chakra-ui/react'
import { Container } from '@components/Container'
import useWindowSize from '@src/hooks/useWindowSize'
import { ArrowLeftIcon, ArrowRightIcon } from '@src/icons'
import { StrapiImageResponsive } from '@src/types/strapi'
import { forwardRef, useImperativeHandle, useRef, useState } from 'react'
import 'swiper/css'
import 'swiper/css/navigation'
import 'swiper/css/pagination'
import { Autoplay, Navigation, Pagination } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/react'
import SwiperClass from 'swiper/types/swiper-class'
import styles from './ProductImageCarousel.module.scss'

interface ProductImageModalCarouselRef {
  open: (item: StrapiImageResponsive) => void
}

interface ProductImageModalCarouselProps {
  onClose?: () => void
  items: StrapiImageResponsive[]
}

const FIRST_IMAGE_INDEX = 0

const ProductImageModalCarousel = forwardRef<ProductImageModalCarouselRef, ProductImageModalCarouselProps>(
  (props, ref) => {
    const { isOpen, onOpen, onClose } = useDisclosure()
    const [item, setItem] = useState<StrapiImageResponsive | null>(null)

    const { isMobile } = useWindowSize()

    const handleClose = () => {
      onClose()
      props.onClose?.()
    }

    useImperativeHandle(ref, () => ({
      open: (item: StrapiImageResponsive) => {
        setItem(item)
        onOpen()
      },
    }))

    if (!item) return null

    const image = isMobile ? item.mobile_image : item.desktop_image

    const previousImage = () => {
      const quantity = props.items.length
      const imageIndex = props.items.findIndex(iteratedItem => iteratedItem.id === item.id)
      const lastItemIndex = quantity - 1

      const previousImageIndex = imageIndex === FIRST_IMAGE_INDEX ? lastItemIndex : imageIndex - 1

      setItem(props.items[previousImageIndex])
    }

    const nextImage = () => {
      const quantity = props.items.length
      const imageIndex = props.items.findIndex(iteratedItem => iteratedItem.id === item.id)
      const lastItemIndex = quantity - 1

      const nextImageIndex = imageIndex === lastItemIndex ? FIRST_IMAGE_INDEX : imageIndex + 1

      setItem(props.items[nextImageIndex])
    }
    // https://staging-gazin-images.gazin.com.br/unsafe/filters:format(webp):quality(75)/${image.url}

  return (
    <Modal isCentered isOpen={isOpen} onClose={handleClose}>
      <ModalOverlay />
      <ModalContent
        borderRadius={"1rem"}
        maxWidth="1072px"
        padding="24px"
      >
        <ModalCloseButton
          zIndex={1}
          color={"white"}
          background={"blue.500"}
          rounded={"full"}
          width="40px"
          height="40px"
          right="40px"
          top="40px"
        />
        <ModalBody
          position="relative"
          borderRadius={"23.45px"}
          width="100%"
          height="100%"
          padding={0}
        >
          <HStack
            display="flex"
            alignItems="center"
            justifyContent="center"
            height="100%"
          >
            <IconButton
              aria-label="Previous image"
              background="white"
              bottom={{ base: "-92px", xl: "inherit" }}
              color="blue.500"
              height="48px"
              icon={<ArrowLeftIcon />}
              left={{ base: "25%", md: "40%", xl: "-92px" }}
              position="absolute"
              rounded="full"
              width="48px"
              onClick={previousImage}
            />
            <Image
              borderRadius={"1rem"}
              src={image.url}
              layout="responsive"
              margin="0 !important"
              sizes="100vh"
              top={0}
              bottom={0}
              alt="Imagem"
            />
            <IconButton
              aria-label="Previous image"
              background="white"
              bottom={{ base: "-92px", xl: "inherit" }}
              color="blue.500"
              height="48px"
              icon={<ArrowRightIcon />}
              position="absolute"
              right={{ base: "25%", md: "40%", xl: "-92px" }}
              rounded="full"
              width="48px"
              onClick={nextImage}
            />
          </HStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
});

ProductImageModalCarousel.displayName = 'ProductImageModalCarousel'

const getSwiperSlidesPerView = (slidesLength: number, quantity: number) => {
  return slidesLength < quantity ? 'auto' : quantity
}

interface ProductImageCarouselProps {
  items: StrapiImageResponsive[]
}

export const ProductImageCarousel = (props: ProductImageCarouselProps) => {
  const previewRef = useRef<ProductImageModalCarouselRef>(null)
  const swiperRef = useRef<SwiperClass>(null)

  const handleOpenPreview = (item: StrapiImageResponsive) => {
    previewRef.current?.open(item)
    swiperRef.current?.autoplay.pause()
  }

  const handleClosePreview = () => {
    swiperRef.current?.autoplay.resume()
  }

  const slidesLength = props.items?.length || 0

  return (
    <Container height={{ base: '200px', md: '254px' }} padding={{ base: undefined, xl: 0 }}>
      <ProductImageModalCarousel ref={previewRef} onClose={handleClosePreview} items={props.items} />
      <Swiper
        onBeforeInit={(swiper: SwiperClass) => {
          const ref = swiperRef as any
          ref.current = swiper
        }}
        modules={[Navigation, Pagination, Autoplay]}
        slidesPerView={5}
        spaceBetween={24}
        slidesPerGroup={1}
        breakpoints={{
          320: {
            slidesPerView: 1.5,
            spaceBetween: 2,
          },
          425: {
            slidesPerView: 2,
            spaceBetween: 8,
          },
          768: {
            slidesPerView: 3,
            spaceBetween: 4,
          },
          1024: {
            slidesPerView: getSwiperSlidesPerView(slidesLength, 3.5),
            spaceBetween: 8,
          },
          1440: {
            slidesPerView: getSwiperSlidesPerView(slidesLength, 5),
            spaceBetween: 24,
          },
        }}
        autoplay={{
          enabled: true,
          delay: 5000,
        }}
        pagination={{
          clickable: true,
          enabled: true,
          bulletClass: `swiper-pagination-bullet ${styles.customBullet}`,
          bulletActiveClass: `swiper-pagination-bullet-active ${styles.customBulletActive}`,
        }}
        className={styles.customCarousel}
      >
        {props.items.map(item => {
          return (
            <SwiperSlide key={item.id} className={styles.customCarouselItem} onClick={() => handleOpenPreview(item)}>
              <Box
                width={{ base: '175px', md: '230px', lg: '254px' }}
                height={{ base: '175px', md: '230px', lg: '254px' }}
                borderRadius={'1rem'}
                borderWidth={'1px'}
                borderColor={'gray.100'}
                position={'relative'}
              >
                <Image
                  src={item.desktop_image.url}
                  layout="responsive"
                  width={400}
                  height={250}
                  style={{
                    borderRadius: '1rem',
                    objectFit: 'cover',
                    objectPosition: 'center',
                  }}
                  />
              </Box>
            </SwiperSlide>
          )
        })}
      </Swiper>
    </Container>
  )
}
